import { Plus, X } from 'lucide-react';
import { useState } from 'react';
import { useRhythmDone, useUpdateProject } from '@/api/studio';
import type { Project, Rhythm, RhythmEvery, RhythmSource } from '@/api/types';
import { formatDate, formatDue } from '@/lib/format';
import { everyLabel, everyOptions, sourceLabel, sourceOptions } from '@/lib/rhythm';

type Row = { id?: string; title: string; every: RhythmEvery | ''; source: RhythmSource; label: string };

const fromRhythm = (r: Rhythm): Row => ({ id: r.id, title: r.title, every: r.every ?? '', source: r.source ?? 'manual', label: r.label ?? '' });

/**
 * «Регулярная работа» of a retainer: what is done on a rhythm and where «Было»/«Будет» come from.
 * Reports, meetings and Linear tasks with a label compute themselves; a manual row is marked
 * «Сделано сегодня» by the studio.
 */
export function RhythmEditor({ p }: { p: Project }) {
  const update = useUpdateProject();
  const done = useRhythmDone();
  const current = p.kind === 'retainer' ? p.rhythm : [];
  const [rows, setRows] = useState<Row[]>(() => current.map(fromRhythm));
  const [saved, setSaved] = useState(false);
  if (p.kind !== 'retainer') return null;

  const setRow = (i: number, v: Partial<Row>) => {
    setSaved(false);
    setRows((xs) => xs.map((x, j) => (j === i ? { ...x, ...v } : x)));
  };
  const valid = rows.every((r) => r.title.trim() && r.every && (r.source !== 'linear' || r.label.trim()));
  const error = (update.error ?? done.error) as Error | null;

  return (
    <section className="frame space-y-3 px-6 py-6" aria-labelledby="rhythm-editor">
      <form
        className="space-y-3 text-sm"
        onSubmit={(e) => {
          e.preventDefault();
          if (!valid || update.isPending) return;
          setSaved(false);
          update.mutate(
            {
              id: p.id,
              patch: {
                rhythm: rows.map((r) => ({ id: r.id, title: r.title.trim(), every: r.every as RhythmEvery, source: r.source, ...(r.source === 'linear' ? { label: r.label.trim() } : {}) })),
              },
            },
            {
              onSuccess: (saved) => {
                if (saved.kind === 'retainer') setRows(saved.rhythm.map(fromRhythm));
                setSaved(true);
              },
            },
          );
        }}
      >
        <h2 id="rhythm-editor" className="text-sm font-semibold">
          Регулярная работа
        </h2>
        <p className="text-muted">«Было» и «Будет» считаются сами: по отчётам, по встречам или по задачам Linear с меткой. Вручную — кнопкой «Сделано сегодня».</p>
        {rows.map((r, i) => {
          const live = current.find((x) => x.id === r.id);
          return (
            <div key={r.id ?? `new-${i}`} className="space-y-1.5 border-t border-line pt-3 first:border-t-0 first:pt-0">
              <div className="grid grid-cols-1 items-center gap-2 sm:grid-cols-[1fr_170px_150px_auto]">
                <input className="field" placeholder="Что делаем" value={r.title} onChange={(e) => setRow(i, { title: e.target.value })} aria-label={`Что делаем ${i + 1}`} />
                <select className="field" value={r.every} onChange={(e) => setRow(i, { every: e.target.value as RhythmEvery })} aria-label={`Как часто ${i + 1}`}>
                  {!r.every && <option value="">Как часто…</option>}
                  {everyOptions.map((o) => (
                    <option key={o} value={o}>
                      {everyLabel[o]}
                    </option>
                  ))}
                </select>
                <select className="field" value={r.source} onChange={(e) => setRow(i, { source: e.target.value as RhythmSource })} aria-label={`Источник ${i + 1}`}>
                  {sourceOptions.map((o) => (
                    <option key={o} value={o}>
                      {sourceLabel[o]}
                    </option>
                  ))}
                </select>
                <button type="button" className="btn-ghost size-8 px-0" aria-label={`Убрать работу ${i + 1}`} onClick={() => (setSaved(false), setRows((xs) => xs.filter((_, j) => j !== i)))}>
                  <X className="size-4" />
                </button>
              </div>
              {r.source === 'linear' && (
                <div>
                  <input className="field" placeholder="rhythm:seo" value={r.label} onChange={(e) => setRow(i, { label: e.target.value })} aria-label={`Метка Linear ${i + 1}`} />
                  <p className="mt-1 text-xs text-muted">Заведите в Linear повторяющиеся задачи с этой меткой в связанном проекте: «Было» — когда закрыта последняя, «Будет» — срок открытой.</p>
                </div>
              )}
              {r.source === 'manual' && live && (
                <div className="flex flex-wrap items-center gap-3 text-xs text-muted">
                  <span>
                    Было: {live.lastDone ? formatDate(live.lastDone) : '—'}
                    {live.next ? `, будет ${formatDue(live.next)}` : ''}
                  </span>
                  <button type="button" className="btn-secondary h-7 px-2" disabled={done.isPending} onClick={() => done.mutate({ projectId: p.id, rhythmId: live.id })}>
                    Сделано сегодня
                  </button>
                </div>
              )}
            </div>
          );
        })}
        <button type="button" className="btn-ghost h-7 px-2 text-xs" onClick={() => (setSaved(false), setRows((xs) => [...xs, { title: '', every: 'week', source: 'manual', label: '' }]))}>
          <Plus className="size-3.5" /> Добавить
        </button>
        {error && (
          <p role="alert" className="text-sm text-overdue">
            {error.message}
          </p>
        )}
        {saved && !update.error && <p className="text-sm text-done">Сохранено</p>}
        <button className="btn-primary" disabled={!valid || update.isPending}>
          Сохранить регулярную работу
        </button>
      </form>
    </section>
  );
}
